'use es6';

import { IsMobileContext } from 'pricing-pages-unified-ui/components/product-pages/mobileUtils';
import { MAX_VISIBLE_PURCHASE_MOTIONS_PA } from 'pricing-pages-unified-ui/constants/MaxVisiblePurchaseMotions';
import { APP, SCREENS } from 'pricing-pages-unified-ui/constants/Tracking';
import { activeCurrencySelector } from 'pricing-pages-unified-ui/selectors/CurrencySelectors';
import { productBasedOfferIdsWithMandatorySkuSelector } from 'pricing-pages-unified-ui/selectors/ProductBasedOffersSelectors';
import { purchaseMotionsSelector } from 'pricing-pages-unified-ui/selectors/PurchaseMotionSelectors';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { checkoutProductConfigurationsSelector, productVersionConfigsWithUnitsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasHigherProductLevelInProductInventorySelector, hasProductVersionInProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { highlightedSkuIdsSelector, productForSkuIdSelector, upgradeProductSelector } from '../../selectors/ProductsSelectors';
const buttonUses = {
  primaryButtonUse: 'primary',
  secondaryButtonUse: 'secondary'
}; // Common props that are used for merch card AuthedCTAs, and the loading state buttons

export const useMerchCardAuthedCTAsProps = ({
  additionalUnitQuantity,
  skuId,
  style = {},
  trackingSource
}) => {
  const isMobile = useContext(IsMobileContext);
  const {
    productLevel,
    productLine
  } = useSelector(state => productForSkuIdSelector(state, {
    skuId
  }) || {});
  const apiName = useSelector(state => {
    const product = productForSkuIdSelector(state, {
      skuId
    });
    return product && product.apiName;
  });
  const purchaseMotions = useSelector(state => purchaseMotionsSelector(state, {
    skuId
  })); // Default to 1 because we show a disabled button for 0 purchase motions

  const numPurchaseMotions = Math.min(purchaseMotions.length, MAX_VISIBLE_PURCHASE_MOTIONS_PA(productLine)) || 1;
  const maxPurchaseMotionsOnPage = useSelector(state => {
    const highlightedSkuIds = highlightedSkuIdsSelector(state, {
      productLine
    });
    return highlightedSkuIds.reduce((currentMax, tempSkuId) => {
      const length = purchaseMotionsSelector(state, {
        skuId: tempSkuId
      }).length;
      return Math.max(currentMax, Math.min(length, MAX_VISIBLE_PURCHASE_MOTIONS_PA(productLine)));
    }, 0);
  });
  let updatedStyled = style;

  if (isMobile) {
    updatedStyled = Object.assign({}, style, {
      minHeight: 'initial'
    });
  }

  const upgradeProduct = useSelector(state => upgradeProductSelector(state, {
    skuIds: [skuId]
  }));
  const upgradeData = {
    app: APP,
    screen: SCREENS.PRODUCTS_PLANS,
    uniqueId: `${trackingSource}-${upgradeProduct}`,
    upgradeProduct
  };
  const productConfigurations = useSelector(state => checkoutProductConfigurationsSelector(state, {
    productConfigurations: productVersionConfigsWithUnitsSelector(state, {
      skuId,
      additionalUnitQuantity
    }),
    subtractOwnedUnitsIfBuyingNoAdditional: true
  }));
  return Object.assign({
    style: updatedStyled,
    purchaseMotions,
    upgradeData,
    productBasedOffers: useSelector(state => productBasedOfferIdsWithMandatorySkuSelector(state, {
      skuIds: [skuId]
    })),
    activeCurrencyCode: useSelector(activeCurrencySelector),
    productConfigurations,
    ownsProduct: useSelector(state => hasProductVersionInProductInventorySelector(state, {
      skuId
    })),
    hasHigherTieredProduct: useSelector(state => hasHigherProductLevelInProductInventorySelector(state, {
      productLevel,
      productLine
    })),
    trackingSource,
    maxPurchaseMotionsOnPage,
    numPurchaseMotions,
    fallbackSeleniumIdentifier: apiName
  }, buttonUses);
};